/**
 * Offline map management storage service
 *
 * Handles offline map tile storage, region management, download progress tracking,
 * and cleanup operations with storage quota awareness and offline-first patterns.
 */

import { db } from '../database/schema.js'
import type { OfflineMap } from '../database/schema.js'
import {
  OfflineFirstStorageService,
  type StorageResult,
  type QueryOptions,
  StorageUtils,
} from './base-storage.js'
import { ValidationUtils } from './validation.js'
import { formatArea, type UserPreferences } from '../lib/coordinates.js'
import { bboxPolygon as turfBboxPolygon, area, booleanOverlap, booleanWithin } from '@turf/turf'
import { tileCacheService } from '../services/tile-cache/tile-cache-service.js'

/**
 * Map region creation data
 */
export interface CreateMapRegionData {
  name: string
  bounds: {
    ne: [number, number]
    sw: [number, number]
  }
  zoomLevel: number
  tileCount?: number
  size?: number
  /** TTL in days. 0 or undefined = never expires. */
  ttlDays?: number
  /** Source IDs that were pinned — used to unpin tiles on expiration/deletion. */
  sourceIds?: string[]
}

/**
 * Map download progress
 */
export interface MapDownloadProgress {
  regionId: string
  regionName: string
  totalTiles: number
  downloadedTiles: number
  failedTiles: number
  progress: number // 0-100
  status: 'pending' | 'downloading' | 'completed' | 'failed' | 'paused'
  estimatedSize: number
  downloadedSize: number
  errors?: string[]
}

/**
 * Map region with calculated metrics
 */
export interface MapRegionWithMetrics extends OfflineMap {
  formattedSize?: string
  formattedArea?: string
  isExpired?: boolean
  daysUntilExpiry?: number
  tilesPerSquareKm?: number
}

/**
 * Storage cleanup recommendations
 */
export interface StorageCleanupRecommendation {
  canCleanup: boolean
  totalSize: number
  availableSpace?: number | undefined
  recommendations: {
    type: 'expired' | 'large' | 'unused' | 'duplicate'
    regions: OfflineMap[]
    potentialSavings: number
    description: string
  }[]
}

/**
 * Map region filters
 */
export interface MapRegionFilters {
  expired?: boolean
  sizeRange?: {
    min?: number
    max?: number
  }
  zoomLevel?: number
  downloadedAfter?: string
  downloadedBefore?: string
}

/**
 * Tile calculation utilities
 */
export class TileCalculator {
  /**
   * Calculate number of tiles for a bounding box and zoom level
   */
  static calculateTileCount(
    bounds: { ne: [number, number]; sw: [number, number] },
    zoomLevel: number
  ): number {
    const { ne, sw } = bounds

    // Convert lat/lng to tile coordinates
    const neTile = this.latLngToTile(ne[0], ne[1], zoomLevel)
    const swTile = this.latLngToTile(sw[0], sw[1], zoomLevel)

    const tileWidth = Math.abs(neTile.x - swTile.x) + 1
    const tileHeight = Math.abs(neTile.y - swTile.y) + 1

    return tileWidth * tileHeight
  }

  /**
   * Estimate storage size for tiles
   */
  static estimateSize(tileCount: number, avgTileSize = 15000): number {
    return tileCount * avgTileSize // 15KB average per tile
  }

  /**
   * Calculate area of bounding box in square kilometers
   */
  static calculateArea(bounds: { ne: [number, number]; sw: [number, number] }): number {
    const bbox = [
      bounds.sw[1], // west
      bounds.sw[0], // south
      bounds.ne[1], // east
      bounds.ne[0], // north
    ] as [number, number, number, number]

    const bboxPoly = turfBboxPolygon(bbox)
    return area(bboxPoly) / 1000000
  }

  /**
   * Convert lat/lng to tile coordinates
   */
  private static latLngToTile(lat: number, lng: number, zoom: number): { x: number; y: number } {
    const n = Math.pow(2, zoom)
    const x = Math.floor(((lng + 180) / 360) * n)
    const y = Math.floor(
      ((1 -
        Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) /
        2) *
        n
    )
    return { x, y }
  }
}

/**
 * Map region validation
 */
function validateMapRegion(
  data: Partial<OfflineMap>,
  context: 'create' | 'update' = 'create'
): { isValid: boolean; errors: import('./validation.js').ValidationError[] } {
  const errors: import('./validation.js').ValidationError[] = []

  if (context === 'create') {
    if (!data.name || !ValidationUtils.isValidStringLength(data.name, 1, 100)) {
      errors.push(
        ValidationUtils.createError(
          'name',
          'Map region name must be 1-100 characters',
          'INVALID_LENGTH'
        )
      )
    }

    if (!data.bounds) {
      errors.push(ValidationUtils.createError('bounds', 'Bounds are required', 'REQUIRED_FIELD'))
    } else {
      if (
        !ValidationUtils.isValidCoordinates(data.bounds.ne) ||
        !ValidationUtils.isValidCoordinates(data.bounds.sw)
      ) {
        errors.push(
          ValidationUtils.createError('bounds', 'Invalid bounds coordinates', 'INVALID_COORDINATES')
        )
      }

      // Validate bounds make sense (NE should be northeast of SW)
      if (data.bounds.ne[0] <= data.bounds.sw[0] || data.bounds.ne[1] <= data.bounds.sw[1]) {
        errors.push(
          ValidationUtils.createError(
            'bounds',
            'Invalid bounds: NE must be northeast of SW',
            'INVALID_RANGE'
          )
        )
      }
    }

    if (data.zoomLevel === undefined || data.zoomLevel < 0 || data.zoomLevel > 18) {
      errors.push(
        ValidationUtils.createError(
          'zoomLevel',
          'Zoom level must be between 0 and 18',
          'INVALID_RANGE'
        )
      )
    }
  }

  if (data.size !== undefined && !ValidationUtils.isNonNegativeNumber(data.size)) {
    errors.push(ValidationUtils.createError('size', 'Size must be non-negative', 'INVALID_VALUE'))
  }

  if (data.tileCount !== undefined && !ValidationUtils.isPositiveNumber(data.tileCount)) {
    errors.push(
      ValidationUtils.createError('tileCount', 'Tile count must be positive', 'INVALID_VALUE')
    )
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Offline map storage service
 */
export class MapStorageService extends OfflineFirstStorageService<OfflineMap> {
  private downloadProgress = new Map<string, MapDownloadProgress>()

  constructor() {
    super(db.offlineMaps, 'offlineMap')
  }

  /**
   * Create a new map region with validation
   */
  async createMapRegion(data: CreateMapRegionData): Promise<StorageResult<OfflineMap>> {
    const validation = validateMapRegion(data, 'create')
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.executeOperation(async () => {
      const id = this.generateId()
      const now = new Date().toISOString()

      // Calculate tiles and size if not provided
      const tileCount =
        data.tileCount ?? TileCalculator.calculateTileCount(data.bounds, data.zoomLevel)
      const estimatedSize = data.size ?? TileCalculator.estimateSize(tileCount)

      const expiresAt =
        data.ttlDays != null && data.ttlDays > 0
          ? new Date(Date.now() + data.ttlDays * 24 * 60 * 60 * 1000).toISOString()
          : ''

      const mapRegion: OfflineMap = {
        id,
        name: data.name,
        bounds: data.bounds,
        zoomLevel: data.zoomLevel,
        tileCount,
        downloadedAt: now,
        expiresAt,
        size: estimatedSize,
        ...(data.sourceIds && { sourceIds: data.sourceIds }),
      }

      await this.table.add(mapRegion)
      return mapRegion
    }, 'create map region')
  }

  /**
   * Get map region with calculated metrics
   */
  async getMapRegionWithMetrics(
    regionId: string,
    preferences?: UserPreferences
  ): Promise<StorageResult<MapRegionWithMetrics | undefined>> {
    return this.executeOperation(async () => {
      const region = await this.table.get(regionId)
      if (!region) {
        return undefined
      }

      const now = new Date()
      const expiryDate = new Date(region.expiresAt)
      const isExpired = now > expiryDate
      const daysUntilExpiry = Math.ceil(
        (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      )

      const area = TileCalculator.calculateArea(region.bounds)
      const tilesPerSquareKm = region.tileCount / area

      const regionWithMetrics: MapRegionWithMetrics = {
        ...region,
        isExpired,
        daysUntilExpiry,
        tilesPerSquareKm,
      }

      if (preferences) {
        regionWithMetrics.formattedSize = StorageUtils.formatStorageSize(region.size)
        regionWithMetrics.formattedArea = formatArea(area * 1000000, preferences) // Convert back to square meters
      }

      return regionWithMetrics
    }, 'get map region with metrics')
  }

  /**
   * Get all map regions with filters
   */
  async getMapRegions(
    filters: MapRegionFilters = {},
    options: QueryOptions = {}
  ): Promise<StorageResult<OfflineMap[]>> {
    return this.executeOperation(async () => {
      let regions = await this.table.toArray()

      // Apply filters
      if (filters.expired !== undefined) {
        const now = new Date()
        regions = regions.filter(region => {
          if (!region.expiresAt) return false // never expires
          const isExpired = now > new Date(region.expiresAt)
          return filters.expired ? isExpired : !isExpired
        })
      }

      if (filters.sizeRange) {
        if (filters.sizeRange.min) {
          regions = regions.filter(region => region.size >= filters.sizeRange!.min!)
        }
        if (filters.sizeRange.max) {
          regions = regions.filter(region => region.size <= filters.sizeRange!.max!)
        }
      }

      if (filters.zoomLevel !== undefined) {
        regions = regions.filter(region => region.zoomLevel === filters.zoomLevel)
      }

      if (filters.downloadedAfter) {
        const afterDate = new Date(filters.downloadedAfter)
        regions = regions.filter(region => new Date(region.downloadedAt) > afterDate)
      }

      if (filters.downloadedBefore) {
        const beforeDate = new Date(filters.downloadedBefore)
        regions = regions.filter(region => new Date(region.downloadedAt) < beforeDate)
      }

      // Apply sorting
      if (options.orderBy) {
        regions.sort((a, b) => {
          const aVal = a[options.orderBy! as keyof OfflineMap]
          const bVal = b[options.orderBy! as keyof OfflineMap]
          const result = (aVal ?? '') > (bVal ?? '') ? 1 : (aVal ?? '') < (bVal ?? '') ? -1 : 0
          return options.orderDirection === 'desc' ? -result : result
        })
      }

      // Apply pagination
      if (options.offset) {
        regions = regions.slice(options.offset)
      }
      if (options.limit) {
        regions = regions.slice(0, options.limit)
      }

      return regions
    }, 'get map regions')
  }

  /**
   * Find overlapping map regions
   */
  async findOverlappingRegions(bounds: {
    ne: [number, number]
    sw: [number, number]
  }): Promise<StorageResult<OfflineMap[]>> {
    return this.executeOperation(async () => {
      const queryBbox = [
        bounds.sw[1], // west
        bounds.sw[0], // south
        bounds.ne[1], // east
        bounds.ne[0], // north
      ] as [number, number, number, number]

      const queryPolygon = turfBboxPolygon(queryBbox)

      const allRegions = await this.table.toArray()

      const overlappingRegions = allRegions.filter(region => {
        const regionBbox = [
          region.bounds.sw[1], // west
          region.bounds.sw[0], // south
          region.bounds.ne[1], // east
          region.bounds.ne[0], // north
        ] as [number, number, number, number]

        const regionPolygon = turfBboxPolygon(regionBbox)

        try {
          /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean OR logic */
          return (
            booleanOverlap(queryPolygon, regionPolygon) ||
            booleanWithin(queryPolygon, regionPolygon) ||
            booleanWithin(regionPolygon, queryPolygon)
          )
          /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
        } catch (e) {
          // Fallback to simple bounds check if Turf.js fails
          return !(
            bounds.ne[0] < region.bounds.sw[0] ||
            bounds.sw[0] > region.bounds.ne[0] ||
            bounds.ne[1] < region.bounds.sw[1] ||
            bounds.sw[1] > region.bounds.ne[1]
          )
        }
      })

      return overlappingRegions
    }, 'find overlapping regions')
  }

  /**
   * Start map download with progress tracking
   */
  async startMapDownload(regionId: string): Promise<StorageResult<MapDownloadProgress>> {
    return this.executeOperation(async () => {
      const region = await this.table.get(regionId)
      if (!region) {
        throw new Error('Map region not found')
      }

      const progress: MapDownloadProgress = {
        regionId,
        regionName: region.name,
        totalTiles: region.tileCount,
        downloadedTiles: 0,
        failedTiles: 0,
        progress: 0,
        status: 'downloading',
        estimatedSize: region.size,
        downloadedSize: 0,
        errors: [],
      }

      this.downloadProgress.set(regionId, progress)
      return progress
    }, 'start map download')
  }

  /**
   * Update download progress
   */
  async updateDownloadProgress(
    regionId: string,
    downloadedTiles: number,
    failedTiles: number = 0,
    downloadedSize: number = 0,
    errors: string[] = []
  ): Promise<StorageResult<MapDownloadProgress>> {
    return this.executeOperation(async () => {
      const progress = this.downloadProgress.get(regionId)
      if (!progress) {
        throw new Error('Download progress not found')
      }

      progress.downloadedTiles = downloadedTiles
      progress.failedTiles = failedTiles
      progress.downloadedSize = downloadedSize
      progress.errors = errors
      progress.progress = (downloadedTiles / progress.totalTiles) * 100

      if (downloadedTiles + failedTiles >= progress.totalTiles) {
        if (failedTiles === 0) {
          progress.status = 'completed'
        } else if (downloadedTiles === 0) {
          progress.status = 'failed'
        } else {
          progress.status = 'completed' // Partial completion
        }

        // Update region with actual size
        await this.update(regionId, { size: downloadedSize })
      }

      return progress
    }, 'update download progress')
  }

  /**
   * Get download progress
   */
  getDownloadProgress(regionId: string): MapDownloadProgress | undefined {
    return this.downloadProgress.get(regionId)
  }

  /**
   * Cancel download
   */
  async cancelDownload(regionId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const progress = this.downloadProgress.get(regionId)
      if (progress) {
        progress.status = 'failed'
      }

      // Clean up partial download
      await this.deleteById(regionId)
      this.downloadProgress.delete(regionId)
      return undefined
    }, 'cancel download')
  }

  /**
   * Update region expiry
   */
  async updateExpiry(regionId: string, expiresAt: string): Promise<StorageResult<OfflineMap>> {
    return this.update(regionId, { expiresAt })
  }

  /**
   * Get expired regions
   */
  async getExpiredRegions(): Promise<StorageResult<OfflineMap[]>> {
    return this.getMapRegions({ expired: true })
  }

  /**
   * Clean up expired regions
   */
  async cleanupExpiredRegions(): Promise<
    StorageResult<{ deletedCount: number; freedSpace: number }>
  > {
    return this.executeOperation(async () => {
      const expiredResult = await this.getExpiredRegions()
      if (!expiredResult.success || !expiredResult.data) {
        return { deletedCount: 0, freedSpace: 0 }
      }

      const expired = expiredResult.data
      let freedSpace = 0

      for (const region of expired) {
        freedSpace += region.size
        if (region.sourceIds) {
          for (const sourceId of region.sourceIds) {
            await tileCacheService.unpinSource(sourceId)
          }
        }
        await this.deleteById(region.id)
      }

      return {
        deletedCount: expired.length,
        freedSpace,
      }
    }, 'cleanup expired regions')
  }

  /**
   * Delete a specific region and unpin its associated tiles.
   */
  async deleteRegion(regionId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      const region = await this.table.get(regionId)
      if (!region) return
      if (region.sourceIds) {
        for (const sourceId of region.sourceIds) {
          await tileCacheService.unpinSource(sourceId)
        }
      }
      await this.deleteById(regionId)
    }, 'delete region')
  }

  /**
   * Get storage cleanup recommendations
   */
  async getCleanupRecommendations(): Promise<StorageResult<StorageCleanupRecommendation>> {
    return this.executeOperation(async () => {
      const allRegions = await this.table.toArray()
      const storageEstimate = await StorageUtils.getStorageEstimate()

      const now = new Date()
      const expiredRegions = allRegions.filter(region => now > new Date(region.expiresAt))

      // Large regions (> 100MB)
      const largeRegions = allRegions.filter(region => region.size > 100 * 1024 * 1024)

      // Unused regions (downloaded more than 60 days ago, not expired)
      const unusedRegions = allRegions.filter(region => {
        const downloadDate = new Date(region.downloadedAt)
        const daysSinceDownload = (now.getTime() - downloadDate.getTime()) / (1000 * 60 * 60 * 24)
        return daysSinceDownload > 60 && now < new Date(region.expiresAt)
      })

      // Potential duplicate regions (overlapping significantly)
      const duplicateRegions: OfflineMap[] = []
      for (let i = 0; i < allRegions.length; i++) {
        for (let j = i + 1; j < allRegions.length; j++) {
          const region1 = allRegions[i]
          const region2 = allRegions[j]

          if (region1.zoomLevel === region2.zoomLevel) {
            const overlap = await this.findOverlappingRegions(region1.bounds)
            if (overlap.success && overlap.data?.includes(region2)) {
              // Calculate overlap percentage (simplified)
              const area1 = TileCalculator.calculateArea(region1.bounds)
              const area2 = TileCalculator.calculateArea(region2.bounds)
              if (Math.min(area1, area2) / Math.max(area1, area2) > 0.8) {
                duplicateRegions.push(region2) // Keep region1, mark region2 as duplicate
              }
            }
          }
        }
      }

      const totalSize = allRegions.reduce((sum, region) => sum + region.size, 0)

      const recommendations = [
        {
          type: 'expired' as const,
          regions: expiredRegions,
          potentialSavings: expiredRegions.reduce((sum, region) => sum + region.size, 0),
          description: `${expiredRegions.length} expired regions`,
        },
        {
          type: 'large' as const,
          regions: largeRegions,
          potentialSavings: largeRegions.reduce((sum, region) => sum + region.size, 0),
          description: `${largeRegions.length} large regions (>100MB)`,
        },
        {
          type: 'unused' as const,
          regions: unusedRegions,
          potentialSavings: unusedRegions.reduce((sum, region) => sum + region.size, 0),
          description: `${unusedRegions.length} unused regions (>60 days old)`,
        },
        {
          type: 'duplicate' as const,
          regions: duplicateRegions,
          potentialSavings: duplicateRegions.reduce((sum, region) => sum + region.size, 0),
          description: `${duplicateRegions.length} potential duplicate regions`,
        },
      ].filter(rec => rec.regions.length > 0)

      return {
        canCleanup: recommendations.length > 0,
        totalSize,
        availableSpace: storageEstimate.available,
        recommendations,
      }
    }, 'get cleanup recommendations')
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<
    StorageResult<{
      totalRegions: number
      totalSize: number
      totalTiles: number
      expiredRegions: number
      expiredSize: number
      averageRegionSize: number
      largestRegion: OfflineMap | null
      oldestRegion: OfflineMap | null
    }>
  > {
    return this.executeOperation(async () => {
      const regions = await this.table.toArray()
      const now = new Date()

      const totalRegions = regions.length
      const totalSize = regions.reduce((sum, region) => sum + region.size, 0)
      const totalTiles = regions.reduce((sum, region) => sum + region.tileCount, 0)

      const expiredRegions = regions.filter(region => now > new Date(region.expiresAt))
      const expiredSize = expiredRegions.reduce((sum, region) => sum + region.size, 0)

      const averageRegionSize = totalRegions > 0 ? totalSize / totalRegions : 0

      const largestRegion = regions.reduce(
        (largest, region) => {
          return !largest || region.size > largest.size ? region : largest
        },
        null as OfflineMap | null
      )

      const oldestRegion = regions.reduce(
        (oldest, region) => {
          return !oldest || new Date(region.downloadedAt) < new Date(oldest.downloadedAt)
            ? region
            : oldest
        },
        null as OfflineMap | null
      )

      return {
        totalRegions,
        totalSize,
        totalTiles,
        expiredRegions: expiredRegions.length,
        expiredSize,
        averageRegionSize,
        largestRegion,
        oldestRegion,
      }
    }, 'get storage stats')
  }
}

// Create and export singleton instance
export const mapStorage = new MapStorageService()
